import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { Dispatch, FormEvent, SetStateAction, useCallback } from 'react';
import { toast } from '@/ui/toast';

type GroupEditorBaseData = {
    id: string;
    name: string;
};

const groupEditorActionsLogger = createClientLogger({
    scope: 'group-editor-actions',
});

type OpenedGroupBase = {
    id: string;
};

type UseGroupEditorRenameActionArgs<
    TGroupEditorData extends GroupEditorBaseData,
    TOpenedGroup extends OpenedGroupBase,
    TRenameResponse extends { id: string; name: string },
> = {
    groupEditorData: TGroupEditorData | null;
    groupEditorNameDraft: string;
    accessToken: string;
    extractApiErrorMessage: (error: unknown) => string | null;
    setGroupEditorData: Dispatch<SetStateAction<TGroupEditorData | null>>;
    setGroupEditorNameDraft: Dispatch<SetStateAction<string>>;
    setOpenedGroupEditor: Dispatch<SetStateAction<TOpenedGroup | null>>;
    setIsGroupEditorNameSaving: Dispatch<SetStateAction<boolean>>;
    renameGroupRequest: (
        groupId: string,
        payload: { name: string },
        options: { authToken: string },
    ) => Promise<{ data: TRenameResponse }>;
    mapGroupEditorDataAfterRename: (currentData: TGroupEditorData, response: TRenameResponse) => TGroupEditorData;
    mapOpenedGroupAfterRename: (currentGroup: TOpenedGroup, response: TRenameResponse) => TOpenedGroup;
    refreshAfterRename: () => Promise<void>;
    messages?: {
        emptyNameWarning?: string;
        success?: string;
        error?: string;
    };
};

export const useGroupEditorRenameAction = <
    TGroupEditorData extends GroupEditorBaseData,
    TOpenedGroup extends OpenedGroupBase,
    TRenameResponse extends { id: string; name: string },
>({
    groupEditorData,
    groupEditorNameDraft,
    accessToken,
    extractApiErrorMessage,
    setGroupEditorData,
    setGroupEditorNameDraft,
    setOpenedGroupEditor,
    setIsGroupEditorNameSaving,
    renameGroupRequest,
    mapGroupEditorDataAfterRename,
    mapOpenedGroupAfterRename,
    refreshAfterRename,
    messages,
}: UseGroupEditorRenameActionArgs<TGroupEditorData, TOpenedGroup, TRenameResponse>) => {
    const emptyNameWarning = messages?.emptyNameWarning || 'Название группы не может быть пустым';
    const successMessage = messages?.success || 'Название группы обновлено';
    const errorMessage = messages?.error || 'Не удалось переименовать группу';

    const renameGroupEditor = useCallback(async () => {
        if (!groupEditorData) {
            return;
        }

        const normalizedGroupName = groupEditorNameDraft.trim();
        if (!normalizedGroupName) {
            toast.warning(emptyNameWarning);
            return;
        }

        if (groupEditorData.name === normalizedGroupName) {
            return;
        }

        setIsGroupEditorNameSaving(true);

        try {
            const response = await renameGroupRequest(
                groupEditorData.id,
                { name: normalizedGroupName },
                { authToken: accessToken },
            );

            setGroupEditorData((currentData) =>
                currentData && currentData.id === response.data.id
                    ? mapGroupEditorDataAfterRename(currentData, response.data)
                    : currentData,
            );
            setGroupEditorNameDraft(response.data.name);
            setOpenedGroupEditor((currentGroup) =>
                currentGroup && currentGroup.id === response.data.id
                    ? mapOpenedGroupAfterRename(currentGroup, response.data)
                    : currentGroup,
            );

            await refreshAfterRename();
            toast.success(successMessage);
        } catch (error) {
            logClientError({
                logger: groupEditorActionsLogger,
                message: '[client.group-management.rename] Failed to rename group.',
                error,
                context: {
                    groupId: groupEditorData.id,
                },
            });
            toast.error(extractApiErrorMessage(error) || errorMessage);
        } finally {
            setIsGroupEditorNameSaving(false);
        }
    }, [
        accessToken,
        emptyNameWarning,
        errorMessage,
        extractApiErrorMessage,
        groupEditorData,
        groupEditorNameDraft,
        mapGroupEditorDataAfterRename,
        mapOpenedGroupAfterRename,
        refreshAfterRename,
        renameGroupRequest,
        setGroupEditorData,
        setGroupEditorNameDraft,
        setIsGroupEditorNameSaving,
        setOpenedGroupEditor,
        successMessage,
    ]);

    const onGroupEditorRenameSubmit = useCallback(
        (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            void renameGroupEditor();
        },
        [renameGroupEditor],
    );

    return {
        renameGroupEditor,
        onGroupEditorRenameSubmit,
    };
};

type UseGroupEditorUngroupActionArgs<
    TGroupEditorData extends GroupEditorBaseData,
    TOpenedGroup extends OpenedGroupBase,
    TSelectionValue extends string,
    TExtractPayload extends Record<string, unknown>,
    TExtractResponse extends { sourceGroupDeleted: boolean },
> = {
    groupEditorData: TGroupEditorData | null;
    selectedValues: TSelectionValue[];
    accessToken: string;
    extractApiErrorMessage: (error: unknown) => string | null;
    setGroupEditorData: Dispatch<SetStateAction<TGroupEditorData | null>>;
    setGroupEditorNameDraft: Dispatch<SetStateAction<string>>;
    setOpenedGroupEditor: Dispatch<SetStateAction<TOpenedGroup | null>>;
    setIsGroupEditorUngrouping: Dispatch<SetStateAction<boolean>>;
    onGroupEditorClose: () => void;
    resetGroupEditorSelectionState: () => void;
    extractFromGroupRequest: (
        groupId: string,
        payload: TExtractPayload,
        options: { authToken: string },
    ) => Promise<{ data: TExtractResponse }>;
    buildExtractPayload: (selectedValues: TSelectionValue[]) => TExtractPayload;
    refreshAfterUngroup: () => Promise<void>;
    loadGroupEditorData: (groupId: string, signal?: AbortSignal) => Promise<TGroupEditorData>;
    mapOpenedGroupAfterRefresh: (currentGroup: TOpenedGroup, refreshedData: TGroupEditorData) => TOpenedGroup;
    messages: {
        emptySelectionWarning: string;
        success: string;
        successDeleted: string;
        error: string;
    };
};

export const useGroupEditorUngroupAction = <
    TGroupEditorData extends GroupEditorBaseData,
    TOpenedGroup extends OpenedGroupBase,
    TSelectionValue extends string,
    TExtractPayload extends Record<string, unknown>,
    TExtractResponse extends { sourceGroupDeleted: boolean },
>({
    groupEditorData,
    selectedValues,
    accessToken,
    extractApiErrorMessage,
    setGroupEditorData,
    setGroupEditorNameDraft,
    setOpenedGroupEditor,
    setIsGroupEditorUngrouping,
    onGroupEditorClose,
    resetGroupEditorSelectionState,
    extractFromGroupRequest,
    buildExtractPayload,
    refreshAfterUngroup,
    loadGroupEditorData,
    mapOpenedGroupAfterRefresh,
    messages,
}: UseGroupEditorUngroupActionArgs<
    TGroupEditorData,
    TOpenedGroup,
    TSelectionValue,
    TExtractPayload,
    TExtractResponse
>) => {
    const onUngroupSelected = useCallback(async () => {
        if (!groupEditorData) {
            return;
        }

        const normalizedSelectedValues = [...new Set(selectedValues)];
        if (!normalizedSelectedValues.length) {
            toast.warning(messages.emptySelectionWarning);
            return;
        }

        setIsGroupEditorUngrouping(true);

        try {
            const response = await extractFromGroupRequest(
                groupEditorData.id,
                buildExtractPayload(normalizedSelectedValues),
                { authToken: accessToken },
            );

            await refreshAfterUngroup();

            if (response.data.sourceGroupDeleted) {
                toast.success(messages.successDeleted);
                onGroupEditorClose();
                return;
            }

            const refreshedGroupData = await loadGroupEditorData(groupEditorData.id);
            setGroupEditorData(refreshedGroupData);
            setGroupEditorNameDraft(refreshedGroupData.name);
            setOpenedGroupEditor((currentGroup) =>
                currentGroup && currentGroup.id === refreshedGroupData.id
                    ? mapOpenedGroupAfterRefresh(currentGroup, refreshedGroupData)
                    : currentGroup,
            );
            resetGroupEditorSelectionState();

            toast.success(messages.success);
        } catch (error) {
            logClientError({
                logger: groupEditorActionsLogger,
                message:
                    '[client.group-management.extract-selected-items] Failed to extract selected items from group.',
                error,
                context: {
                    groupId: groupEditorData.id,
                    selectedValues: normalizedSelectedValues.length,
                },
            });
            toast.error(extractApiErrorMessage(error) || messages.error);
        } finally {
            setIsGroupEditorUngrouping(false);
        }
    }, [
        accessToken,
        buildExtractPayload,
        extractApiErrorMessage,
        extractFromGroupRequest,
        groupEditorData,
        loadGroupEditorData,
        mapOpenedGroupAfterRefresh,
        messages,
        onGroupEditorClose,
        refreshAfterUngroup,
        resetGroupEditorSelectionState,
        selectedValues,
        setGroupEditorData,
        setGroupEditorNameDraft,
        setIsGroupEditorUngrouping,
        setOpenedGroupEditor,
    ]);

    return {
        onUngroupSelected,
    };
};
